import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../../common';

const FlowUploadModal = ({ tenant, onClose, onSuccess }) => {
  const [flowId, setFlowId] = useState('');
  const [yamlContent, setYamlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verifica se é arquivo YAML
    if (!file.name.endsWith('.yaml') && !file.name.endsWith('.yml')) {
      setError('Por favor, selecione um arquivo YAML (.yaml ou .yml)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setYamlContent(event.target.result);
      setError('');
      
      // Extrai o flowId do nome do arquivo (remove extensão)
      const fileName = file.name.replace(/\.(yaml|yml)$/, '');
      if (!flowId) {
        setFlowId(fileName);
      }
    };
    reader.onerror = () => {
      setError('Erro ao ler o arquivo');
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!flowId.trim()) {
      setError('ID do fluxo é obrigatório');
      return;
    }

    if (!yamlContent.trim()) {
      setError('Conteúdo YAML é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onSuccess(flowId, yamlContent);
    } catch (err) {
      setError(err.message || 'Erro ao fazer upload do fluxo');
      setLoading(false);
    }
  };

  // Função para aplicar syntax highlighting básico
  const renderYamlWithHighlight = () => {
    if (!yamlContent) return '';

    return yamlContent.split('\n').map((line, index) => {
      let highlightedLine = line;

      // Comentários (#)
      if (line.trim().startsWith('#')) {
        return (
          <div key={index} className="text-gray-500 italic">
            {line}
          </div>
        );
      }

      // Chaves (keys:)
      const keyMatch = line.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/);
      if (keyMatch) {
        const indent = keyMatch[1];
        const key = keyMatch[2];
        const rest = line.substring(keyMatch[0].length);
        
        return (
          <div key={index}>
            <span>{indent}</span>
            <span className="text-blue-600 font-semibold">{key}</span>
            <span>:</span>
            <span className="text-green-600">{rest}</span>
          </div>
        );
      }

      // Strings entre aspas
      highlightedLine = line.replace(/"([^"]*)"/g, '<span class="text-green-600">"$1"</span>');
      highlightedLine = highlightedLine.replace(/'([^']*)'/g, '<span class="text-green-600">\'$1\'</span>');

      // Números
      highlightedLine = highlightedLine.replace(/\b(\d+)\b/g, '<span class="text-orange-600">$1</span>');

      // Booleanos
      highlightedLine = highlightedLine.replace(/\b(true|false|yes|no|null)\b/g, '<span class="text-purple-600">$1</span>');

      return (
        <div key={index} dangerouslySetInnerHTML={{ __html: highlightedLine || ' ' }} />
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload de Fluxo YAML</h2>
            <p className="text-sm text-gray-600 mt-1">Tenant: {tenant.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] custom-scrollbar">
          <div className="space-y-6">
            {/* Flow ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID do Fluxo *
              </label>
              <input
                type="text"
                value={flowId}
                onChange={(e) => setFlowId(e.target.value)}
                placeholder="real_estate_lead"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Identificador único do fluxo (ex: real_estate_lead, customer_support)
              </p>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arquivo YAML *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                <input
                  type="file"
                  accept=".yaml,.yml"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="yaml-upload"
                />
                <label htmlFor="yaml-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-1">
                    Clique para selecionar um arquivo YAML
                  </p>
                  <p className="text-xs text-gray-500">
                    Arquivos .yaml ou .yml
                  </p>
                </label>
              </div>
            </div>

            {/* YAML Content Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conteúdo YAML *
              </label>
              
              <div className="relative">
                {/* Textarea invisível para input */}
                <textarea
                  value={yamlContent}
                  onChange={(e) => setYamlContent(e.target.value)}
                  placeholder="Cole ou edite o conteúdo YAML aqui..."
                  rows={16}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent font-mono text-sm text-transparent caret-blue-600 bg-transparent resize-none relative z-10"
                  spellCheck="false"
                  required
                  style={{
                    caretColor: '#2563eb',
                    color: 'transparent'
                  }}
                />
                
                {/* Display com syntax highlighting */}
                <div className="absolute inset-0 px-4 py-3 pointer-events-none overflow-auto font-mono text-sm leading-relaxed">
                  {yamlContent ? (
                    renderYamlWithHighlight()
                  ) : (
                    <div className="text-gray-400">Cole ou edite o conteúdo YAML aqui...</div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-600 rounded"></div>
                  <span>Chaves</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-600 rounded"></div>
                  <span>Valores</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-orange-600 rounded"></div>
                  <span>Números</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-purple-600 rounded"></div>
                  <span>Booleanos</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gray-500 rounded"></div>
                  <span>Comentários</span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            leftIcon={<Upload className="w-4 h-4" />}
          >
            Fazer Upload
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FlowUploadModal;